import React from 'react';
import {
  RegistrationScreen,
  formatDob,
  formatMobileNational,
  type RegistrationConfig,
} from './registration-common.js';

/**
 * SCRN 17 — Open24 Registration (EDS personal customers). Mirrors the Figma
 * `update-reg-eds` design: name / DoB / mother's maiden name / mobile mandatory
 * identity items (each with a "not on file" toggle that reveals the alternative
 * question — other products / landline), then an active-Visa-debit-card +
 * joint-accounts check, the CCF-16 2FA read-back, mandatory scripting and
 * "Transfer to automated self-service". Reached after a deregistration when the
 * online-card type is OPEN24 (CCF-8/CCF-46). The Open24-specific sections are
 * driven by `open24Extras` on the shared {@link RegistrationScreen}; the mobile
 * number is shown in FULL national form WITHOUT the country code (e.g.
 * "085 8076526") per the Figma.
 */
const CONFIG: RegistrationConfig = {
  screenCode: 'SCRN-17',
  heading: 'Open24 registration',
  open24Extras: true,
  // #774 (@dylan219, 2026-09-11): the mobile-number question is always markable
  // Pass/Fail with no alternate question; a mobile FAIL auto-fails 2FA (outcome
  // deferred until all other questions are completed). Supersedes the mobile
  // "not on file" → landline-alternate behaviour of #348/#656/#662/#372.
  mobileQuestionAlwaysMarkable: true,
  mandatory: [
    { key: 'name', label: 'Full name', value: (_q, name) => name },
    { key: 'dob', label: 'Date of birth', value: (q) => formatDob(q.Dob) },
    {
      key: 'maiden',
      label: "Mother's maiden name",
      value: (q) => q.MothersMaidenName,
    },
    {
      key: 'mobile',
      label: 'Mobile number',
      // Full national number without the +353 country code (Figma).
      value: (q) => formatMobileNational(q.MobileNumber) ?? q.MaskedMobile,
    },
  ],
  // Active Visa debit card + joint accounts are confirmatory questions: only
  // markable when an answer is on file, a Fail refers to branch, and none is
  // individually mandatory (requiredAdditional: 0) — faithful to the Figma.
  // Both render as discrete list items (`answerList`, issue #348 — mirroring
  // `SecurityScreen.tsx`'s `resolveList`/`IdentityQuestion.listValue`
  // convention, issue #291) rather than one joined string. Joint accounts are
  // overridden by `RegistrationScreen` with the authoritative
  // `/security/joint-associations` source (issue #318); the active-Visa-debit
  // list is the server-enriched `/security/questions` `ActiveVisaDebitCards`
  // field, which #345/#388 populate from a real `card/list` call — the SAME
  // source the ECL Medium/High security screens consume, and (unlike the
  // MEDIUM-gated client `/cards` fetch this previously used, #348) available on
  // the level-`none` registration screen where `/cards` 403'd (issue #407).
  additional: [
    {
      key: 'visaDebit',
      label: 'Do you have an active Visa debit card?',
      // Figma (`update-reg-eds`, SCRN-17, issue #367): rendered as a DROPDOWN of
      // the customer's active Visa-debit card numbers (last-4 masked) for the
      // agent to verify against — placeholder "Active Visa cards numbers" — not
      // a bullet list. The entries are the authoritative, real-gateway-backed
      // active-Visa-debit cards from the server-enriched
      // `/security/questions.ActiveVisaDebitCards` field (#345/#388/#407), which
      // `RegistrationScreen` threads into `ActiveVisaDebitCards` (fail-closed on
      // `ActiveVisaDebitCardsError`) before this resolver runs.
      answerList: (q) => q.ActiveVisaDebitCards,
      answerDropdown: true,
      answerDropdownPlaceholder: 'Active Visa cards numbers',
      hint: 'If yes — confirm the last 4 digits shown below. If no — this is the correct answer.',
    },
    {
      key: 'jointAccounts',
      label: 'Have you any joint accounts held in PTSB?',
      answerList: (q) => q.JointAccounts,
      // Issue #476: when the authoritative `/security/joint-associations`
      // source loads and the customer genuinely has NO joint accounts (an
      // empty `[]`, threaded into `JointAccounts` by `RegistrationScreen`),
      // read "No joint accounts" rather than the ambiguous "Answer not on
      // file" — the latter is reserved for a source that is unavailable /
      // failed to load (an `undefined` list).
      answerListEmptyLabel: 'No joint accounts',
    },
  ],
  requiredAdditional: 0,
  twoFactor: true,
  // #729 (@dylan219, 2026-08-31) — once all questions + 2FA pass, offer the
  // Reset Internet Password option with the scoped Back round-trip, in the
  // same way and under the same rules as Business24 / Broker24 (#653). The
  // underlying gateway reset flow is PTSB-side pending; the option is wired in
  // parallel with their gateway fix and surfaces whatever the gateway returns.
  ipwReset: true,
};

export function Open24RegistrationScreen(): React.JSX.Element {
  return <RegistrationScreen config={CONFIG} />;
}
